import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { updateTextbaustein } from './textbaustein.actions';
import { TextbausteinId, TextbausteinResource } from './textbaustein.model';
import { parse, platzhalterWertType } from './textbaustein.parser';
import { selectTextbausteinById } from './textbaustein.selectors';
import { TextbausteinState } from './textbaustein.state';
import { SubTextFeldName } from '@schir-int-client/register-shared';

@Injectable({ providedIn: 'root' })
export class TextbausteinFacade {
	constructor(private store: Store<TextbausteinState>) {}

	getPreviewText(
		textbausteinId: TextbausteinId,
		platzhalterWerte: { [fieldName: string]: platzhalterWertType },
		vormerkungPrefix: string
	): Observable<string> {
		return combineLatest([this.getTextbausteinById(textbausteinId), this.getSubTexte(platzhalterWerte)]).pipe(
			map(([textbaustein, subTexte]) => {
				if (vormerkungPrefix) {
					let preview = parse(textbaustein, platzhalterWerte, subTexte);
					preview = preview.split('Registerpfandrecht ').join('');
					preview = preview.split('Gesamtregisterpfandrecht ').join('');
					return vormerkungPrefix + ' ' + preview;
				}
				return parse(textbaustein, platzhalterWerte, subTexte);
			})
		);
	}

	getSubTexte(platzhalterWerte: { [fieldName: string]: any }): Observable<{ [subField: string]: string }> {
		return combineLatest([
			this.fillVollstreckung(platzhalterWerte),
			this.fillGesamthaft(platzhalterWerte),
			this.fillZinsen(platzhalterWerte),
			this.fillRangvermerk(platzhalterWerte),
			this.fillMigration(platzhalterWerte),
			this.fillVawVerfuegung(platzhalterWerte),
			this.fillBewilligungVerfuegung(platzhalterWerte),
			this.fillEinschraenkungEigentuemer(platzhalterWerte),
			this.fillOlg(platzhalterWerte),
			this.fillVormerkung(platzhalterWerte),
			this.fillVerzinsung(platzhalterWerte),
			this.fillRangvorbehalt(platzhalterWerte),
			this.fillRangeintrag(platzhalterWerte),
			this.fillVorrangsvorbehalt(platzhalterWerte),
			this.fillVorerbe(platzhalterWerte),
		]).pipe(
			map(
				([
					vollstreckung,
					gesamthaft,
					zinsen,
					rangvermerk,
					migration,
					vawVerfuegung,
					bewilligungVerfuegung,
					einschraenkungEigetuemer,
					olg,
					vormerkung,
					verzinsung,
					rangvorbehalt,
					rangeintrag,
					vorrangsvorbehalt,
					vorerbe,
				]) => {
					return {
						VOLLSTRECKUNG: vollstreckung,
						GESAMTHAFT: gesamthaft,
						ZINSEN: zinsen,
						RANGVERMERK: rangvermerk,
						MIGRATION: migration,
						VAW_VERFUEGUNG: vawVerfuegung,
						BEWILLIGUNG_VERFUEGUNG: bewilligungVerfuegung,
						EINSCHRAENKUNG_EIGENTUEMER: einschraenkungEigetuemer,
						OLG: olg,
						VORMERKUNG: vormerkung,
						VERZINSUNG: verzinsung,
						RANGVORBEHALT: rangvorbehalt,
						RANGEINTRAG: rangeintrag,
						VORRANGSVORBEHALT: vorrangsvorbehalt,
						VORERBE: vorerbe,
					};
				}
			)
		);
	}

	private fillVollstreckung(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.vollstreckung, platzhalterWerte);
	}

	private fillGesamthaft(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.gesamthaft, platzhalterWerte);
	}

	private fillZinsen(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.zinsen, platzhalterWerte);
	}

	private fillRangvermerk(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.rangvermerk, platzhalterWerte);
	}

	private fillMigration(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.migration, platzhalterWerte);
	}

	private fillVawVerfuegung(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.vawVerfuegung, platzhalterWerte);
	}

	private fillBewilligungVerfuegung(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.bewilligungVerfuegung, platzhalterWerte);
	}

	private fillEinschraenkungEigentuemer(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.einschraenkungEigentuemer, platzhalterWerte);
	}

	private fillOlg(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.olg, platzhalterWerte);
	}

	private fillVormerkung(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.vormerkung, platzhalterWerte);
	}

	private fillVerzinsung(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.verzinsung, platzhalterWerte);
	}

	private fillRangvorbehalt(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.rangvorbehalt, platzhalterWerte);
	}

	private fillRangeintrag(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.rangeintrag, platzhalterWerte);
	}

	private fillVorrangsvorbehalt(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.vorrangsvorbehalt, platzhalterWerte);
	}

	private fillVorerbe(platzhalterWerte: any): Observable<string> {
		return this.fill(SubTextFeldName.vorerbe, platzhalterWerte);
	}

	private fill(field: string, platzhalterWerte: any): Observable<string> {
		if (!isNil(platzhalterWerte[field])) {
			return this.fillTextbausteinById(
				platzhalterWerte[field].textbausteinId,
				platzhalterWerte[field].platzhalterWerte
			);
		}
		return of(null);
	}

	private fillTextbausteinById(textbausteinId: TextbausteinId, platzhalterWerte: any): Observable<string> {
		return this.getTextbausteinById(textbausteinId).pipe(
			map(textbaustein => (isNil(textbaustein) ? '' : parse(textbaustein, platzhalterWerte)))
		);
	}

	getTextbausteinById(textbausteinId: TextbausteinId): Observable<TextbausteinResource> {
		return this.store.select(selectTextbausteinById(textbausteinId));
	}

	updateTextbaustein(textbausteinId: TextbausteinId, editedText: string): void {
		if (!isNil(textbausteinId)) {
			this.store.dispatch(updateTextbaustein({ textbausteinId, editedText }));
		}
	}
}
